/*
-------------------------------------------------------------------------------------
Description     : 
    Modal component for new DentistOwner and AssociateDentist users to select their
    services after first sign-in. This is a required step before accessing the dashboard.
-------------------------------------------------------------------------------------
*/

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Stack,
  Checkbox,
  Divider,
  Alert,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { db } from '../../../../backend/firebaseConfig';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { servicesList as defaultServicesList } from '../../organisms/list/ServicesList';

// 30-min slots from 9:00 AM to 6:00 PM
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let m = 540; m <= 1080; m += 30) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    const suffix = h < 12 ? "AM" : "PM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    opts.push({
      value: `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`,
      label: `${h12}:${String(min).padStart(2, "0")} ${suffix}`,
    });
  }
  return opts;
})();

const ServiceSelectionModal = ({
  isOpen,
  onComplete,
  userUid,
  userName
}) => {
  const [masterServicesList, setMasterServicesList] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [availableFrom, setAvailableFrom] = useState("09:00");
  const [availableTo, setAvailableTo] = useState("18:00");

  // Fetch the master services list from Firestore
  useEffect(() => {
    const fetchMasterServices = async () => {
      try {
        setIsLoading(true);
        const servicesRef = doc(db, "services", "servicesList");
        const snapshot = await getDoc(servicesRef);
        
        if (snapshot.exists() && snapshot.data().servicesList) {
          setMasterServicesList(snapshot.data().servicesList);
        } else {
          // Fallback to default services list
          setMasterServicesList(defaultServicesList);
        }
      } catch (err) {
        console.error("Error fetching master services:", err);
        // Use default services list as fallback
        setMasterServicesList(defaultServicesList);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchMasterServices();
    }
  }, [isOpen]);

  // Group services by category
  const groupedServices = masterServicesList.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {});

  // Toggle service selection
  const toggleService = (serviceName) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceName)) {
        return prev.filter(s => s !== serviceName);
      } else {
        return [...prev, serviceName];
      }
    });
  };

  // Toggle all services in a category
  const toggleCategory = (category) => {
    const categoryServiceNames = groupedServices[category].map(s => s.name);
    const allSelected = categoryServiceNames.every(name => selectedServices.includes(name));
    
    if (allSelected) {
      // Deselect all in category
      setSelectedServices(prev => prev.filter(s => !categoryServiceNames.includes(s)));
    } else {
      // Select all in category
      setSelectedServices(prev => {
        const newSelection = [...prev];
        categoryServiceNames.forEach(name => {
          if (!newSelection.includes(name)) {
            newSelection.push(name);
          }
        });
        return newSelection;
      });
    }
  };

  // Toggle select all / deselect all services
  const toggleSelectAll = () => {
    const allSelected = masterServicesList.length > 0 && 
      masterServicesList.every(service => selectedServices.includes(service.name));
    
    if (allSelected) {
      // Deselect all
      setSelectedServices([]);
    } else {
      // Select all
      setSelectedServices(masterServicesList.map(s => s.name));
    }
  };

  // Save selected services
  const handleSaveServices = async () => {
    if (selectedServices.length === 0) {
      setError('Please select at least one service to continue.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      // Filter the master services to only include selected ones
      const selectedServicesList = masterServicesList.filter(service => 
        selectedServices.includes(service.name)
      );

      // Add duration field if missing
      const servicesWithDuration = selectedServicesList.map(service => ({
        ...service,
        duration: service.duration || ""
      }));

      // Save to Firestore under services/{dentistUID}
      const dentistServicesRef = doc(db, "services", userUid);
      await setDoc(dentistServicesRef, { servicesList: servicesWithDuration });

      // Update user document to mark services as selected and save default availability
      const userRef = doc(db, "users", userUid);
      await updateDoc(userRef, {
        hasSelectedServices: true,
        defaultAvailableFrom: availableFrom,
        defaultAvailableTo: availableTo,
      });

      // Call the completion callback
      onComplete();
    } catch (err) {
      console.error("Error saving services:", err);
      setError('Failed to save services. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if all services in a category are selected
  const isCategoryFullySelected = (category) => {
    const categoryServiceNames = groupedServices[category].map(s => s.name);
    return categoryServiceNames.every(name => selectedServices.includes(name));
  };

  // Check if some services in a category are selected
  const isCategoryPartiallySelected = (category) => {
    const categoryServiceNames = groupedServices[category].map(s => s.name);
    const selectedCount = categoryServiceNames.filter(name => selectedServices.includes(name)).length;
    return selectedCount > 0 && selectedCount < categoryServiceNames.length;
  };

  return (
    <Dialog
      open={isOpen}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '8px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Stack spacing={1}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2C251E' }}>
            Welcome, {userName || 'Doctor'}!
          </Typography>
          <Typography variant="body1" sx={{ color: '#59544A' }}>
            Please select the services you offer. This will help patients know what treatments you provide.
          </Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress sx={{ color: '#C5A572' }} />
          </Box>
        ) : (
          <>
            {/* Typical Available Hours */}
            <Box sx={{ mb: 3, p: 2, borderRadius: '12px', backgroundColor: '#F5F1EB', border: '1px solid rgba(197,165,114,0.35)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2C251E', mb: 0.5 }}>
                Typical Available Hours
              </Typography>
              <Typography variant="caption" sx={{ color: '#59544A', display: 'block', mb: 1.5 }}>
                Set your general working hours. Clinic staff will use this as a reference when plotting your schedule.
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>From</InputLabel>
                  <Select
                    label="From"
                    value={availableFrom}
                    onChange={(e) => {
                      setAvailableFrom(e.target.value);
                      if (e.target.value >= availableTo) {
                        const idx = TIME_OPTIONS.findIndex(o => o.value === e.target.value);
                        if (idx < TIME_OPTIONS.length - 1) setAvailableTo(TIME_OPTIONS[idx + 1].value);
                      }
                    }}
                    sx={{ backgroundColor: 'white' }}
                  >
                    {TIME_OPTIONS.filter(o => o.value < "18:00").map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography sx={{ color: '#59544A', fontWeight: 500 }}>to</Typography>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>To</InputLabel>
                  <Select
                    label="To"
                    value={availableTo}
                    onChange={(e) => setAvailableTo(e.target.value)}
                    sx={{ backgroundColor: 'white' }}
                  >
                    {TIME_OPTIONS.filter(o => o.value > availableFrom).map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            {/* Quick Actions */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={masterServicesList.length > 0 && 
                      masterServicesList.every(service => selectedServices.includes(service.name))}
                    onChange={toggleSelectAll}
                    sx={{
                      color: '#C5A572',
                      '&.Mui-checked': {
                        color: '#C5A572',
                      },
                    }}
                  />
                }
                label="Select All"
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    color: '#333',
                    fontSize: '14px',
                  },
                }}
              />
              <Typography variant="body2" sx={{ ml: 'auto', color: '#59544A' }}>
                {selectedServices.length} of {masterServicesList.length} services selected
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Services List */}
            <Box sx={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              border: '1px solid #E0D8C8',
              borderRadius: '12px',
              backgroundColor: '#FAFAFA'
            }}>
              {Object.entries(groupedServices).map(([category, categoryServices], categoryIndex) => (
                <Box key={category}>
                  {/* Category Header */}
                  <Box
                    onClick={() => toggleCategory(category)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      backgroundColor: '#F5F1EB',
                      cursor: 'pointer',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      '&:hover': {
                        backgroundColor: '#EDE8E0'
                      }
                    }}
                  >
                    <Checkbox
                      checked={isCategoryFullySelected(category)}
                      indeterminate={isCategoryPartiallySelected(category)}
                      sx={{
                        color: '#C5A572',
                        '&.Mui-checked': { color: '#C5A572' },
                        '&.MuiCheckbox-indeterminate': { color: '#C5A572' }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleCategory(category)}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2C251E', flex: 1 }}>
                      {category}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#59544A' }}>
                      {categoryServices.filter(s => selectedServices.includes(s.name)).length}/{categoryServices.length}
                    </Typography>
                  </Box>

                  {/* Services in Category */}
                  {categoryServices.map((service, serviceIndex) => (
                    <Box
                      key={service.name}
                      onClick={() => toggleService(service.name)}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '10px 16px 10px 32px',
                        cursor: 'pointer',
                        backgroundColor: selectedServices.includes(service.name) ? 'rgba(197, 165, 114, 0.1)' : 'white',
                        borderBottom: serviceIndex < categoryServices.length - 1 ? '1px solid #F0EBE3' : 'none',
                        '&:hover': {
                          backgroundColor: selectedServices.includes(service.name) 
                            ? 'rgba(197, 165, 114, 0.15)' 
                            : 'rgba(0, 0, 0, 0.02)'
                        }
                      }}
                    >
                      <Checkbox
                        checked={selectedServices.includes(service.name)}
                        sx={{
                          color: '#C5A572',
                          '&.Mui-checked': { color: '#C5A572' },
                          padding: '4px',
                          marginRight: '8px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleService(service.name)}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2C251E' }}>
                          {service.name}
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                  {categoryIndex < Object.keys(groupedServices).length - 1 && (
                    <Divider />
                  )}
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={handleSaveServices}
          disabled={isSaving || isLoading || selectedServices.length === 0}
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircle />}
          sx={{
            backgroundColor: '#C5A572',
            color: 'white',
            padding: '10px 32px',
            borderRadius: '8px',
            fontWeight: 600,
            minWidth: '180px',
            '&:hover': {
              backgroundColor: '#B39462'
            },
            '&:disabled': {
              backgroundColor: '#E0D8C8',
              color: 'white'
            }
          }}
        >
          {isSaving ? 'Saving...' : `Save ${selectedServices.length} Service${selectedServices.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceSelectionModal;

